import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight, BadgeCheck, FileText } from 'lucide-react';
import { motion } from 'framer-motion';
import {
    FaShieldAlt,        // For Zero Depreciation Cover
    FaTools,            // For Engine Protection Cover
    FaTruckPickup,      // For Roadside Assistance
    FaOilCan,           // For Consumables Cover
    FaUserShield,       // For Personal Accident Cover
    FaCloudSunRain,
    FaUserLock,
    FaCarCrash,
    FaCarSide,
} from "react-icons/fa";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const HondaTwoWheelerInsurance = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question: "Is zero depreciation useful for Honda models?",
      answer: "Yes, it’s especially beneficial for premium models like CB Hornet, Unicorn, or Elevate.",
    },
    {
      question: "Can I insure my used Honda bike online?",
      answer: "Yes, most insurers allow online renewal or new policy purchase for used vehicles.",
    },
    {
      question: "Which Honda model has the lowest insurance cost?",
      answer: "Shine and Activa generally come with the lowest premiums due to lower IDV.",
    },
    {
      question: "Is third-party insurance enough for scooters like Activa or Dio?",
      answer: "Legally required, but comprehensive insurance is highly recommended.",
    },
    {
      question: "Can I transfer NCB from another bike?",
      answer: "Yes, as long as the ownership remains with the same person.",
    },
    {
      question: "Is roadside assistance worth adding for scooters?",
      answer: "Yes, especially for flat tyre or battery-related emergencies.",
    },
    {
      question: "What happens if I let my policy lapse?",
      answer: "You may lose NCB and incur inspection or penalty before reinstating cover.",
    },
    {
      question: "Does Honda offer bundled insurance at dealerships?",
      answer: "Often yes, but standalone policies usually offer better pricing and customization.",
    },
    {
      question: "Are Honda EV scooters covered under standard insurance?",
      answer: "EV-specific policies are needed and are available from most major insurers.",
    },
    {
      question: "Do I need engine cover for Honda bikes?",
      answer: "Recommended if you ride in flood-prone zones or rural areas.",
    },
    {
      question: "Can I pay premiums in installments?",
      answer: "Some insurers support EMI filing on annual premiums.",
    },
    {
      question: "Are accessories like crash guards or audio eligible for coverage?",
      answer: "Only if declared and approved at policy purchase.",
    },
    {
      question: "Is insurance available without inspection for Honda bikes?",
      answer: "Yes, if the policy hasn't expired for too long.",
    },
    {
      question: "Are digital claims supported for Honda plans?",
      answer: "Yes, most insurers provide app-based or online claim filing.",
    },
    {
      question: "How long does claim settlement take?",
      answer: "Typically between 7 and 10 working days, depending on paperwork.",
    },
    {
      question: "Is tyre damage covered under standard plans?",
      answer: "Only if caused by an accident. A tyre protection add-on covers more extensive damage.",
    },
    {
      question: "Does TheFinMaster offer policy comparison for Honda?",
      answer: "Yes, you can compare insurers, calculate premiums and buy coverage directly on the site.",
    },
    {
      question: "Is there a fine for riding a Honda without insurance?",
      answer: "Yes, ₹2,000 under the Motor Vehicles Act for first offenders.",
    },
    {
      question: "Does comprehensive cover bike theft?",
      answer: "Yes, theft is covered under a comprehensive plan; FIR and claim formalities apply.",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const cardsNew = [
    {
      title: "Zero Depreciation Cover",
      description: "No deduction on part values, particularly useful for models like Hornet, Unicorn or Elevate.",
      icon: <FaShieldAlt className="text-4xl text-red-500 mb-4" />,
    },
    {
      title: "Engine Protection Cover",
      description: "Helps cover repair costs due to waterlogging or engine damage.",
      icon: <FaTools className="text-4xl text-yellow-600 mb-4" />,
    },
    {
      title: "Roadside Assistance",
      description: "Useful for breakdowns or emergencies such as flat tyre or fuel help.",
      icon: <FaTruckPickup className="text-4xl text-blue-500 mb-4" />,
    },
    {
      title: "Consumables Cover",
      description: "Pays for small parts like nuts, bolts, engine oil and coolant during repairs.",
      icon: <FaOilCan className="text-4xl text-green-600 mb-4" />,
    },
    {
      title: "Personal Accident Cover",
      description: "Provides financial support in case of injury or death due to an accident.",
      icon: <FaUserShield className="text-4xl text-purple-600 mb-4" />,
    },
  ];

  const features = [
    { icon: '🛡️', text: 'Reliable & Accurate Estimates' },
    { icon: '⏱️', text: 'Takes Less Than a Minute' },
    { icon: '📉', text: 'Instantly Shows Premium Breakup' },
    { icon: '📄', text: 'No Paperwork or Documentation' },
    { icon: '📲', text: 'Easy to Use on Any Device' },
    { icon: '💰', text: 'Helps You Save on Insurance' },
  ];
const hondaInsurance = [
    {
      name: "Activa",
      path: "/honda-activa-bike-insurance", 
      image: "/assets/hondaactiva.jpeg",
    },
  {
      name: "Shine 125",
      path: "/honda-shine-125-bike-insurance", 
      image: "/assets/hondashine125.jpeg",
    },
     {
      name: "SP 125",
      path: "/honda-sp-125-bike-insurance", 
      image: "/assets/hondasp125.jpeg",
    },
     {
      name: "Unicorn",
      path: "/honda-unicorn-bike-insurance", 
      image: "/assets/hondaunicorn.jpeg",
    },
     {
      name: "CB200X",
      path: "/honda-cb200x-bike-insurance", 
      image: "/assets/hondacb200x.jpeg",
    },
    {
      name: "CB350 Hness",
      path: "/honda-cb350-hness-bike-insurance", 
      image: "/assets/hondacb350hness.jpeg",
    },
     {
      name: "CB750 Hornet",
      path: "/honda-cb750-hornet-bike-insurance", 
      image: "/assets/hondacb750hornet.jpeg",
    },
  ];
  return (
    <>
      <Helmet>
        <title>Honda Two-Wheeler Insurance in India – Compare Quotes & Buy Online</title>
        <meta
          name="description"
          content="Explore Honda two-wheeler insurance options in India. Compare quotes, customize coverage, and buy online to protect your Honda bike or scooter."
        />
        <meta
          name="keywords"
          content="honda two-wheeler insurance, bike insurance india, honda activa insurance, honda shine insurance, honda hornet insurance, two-wheeler insurance quotes"
        />
          <link rel="canonical" href="https://thefinmaster.com/honda-two-wheeler-insurance" />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
        {/* <link rel="canonical" href="https://thefinmaster.com/honda-two-wheeler-insurance" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard, max-video-preview:-1" />
        <meta property="og:title" content="Honda Two-Wheeler Insurance in India" />
        <meta property="og:description" content="Compare and buy Honda two-wheeler insurance online with ease." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thefinmaster.com/honda-two-wheeler-insurance" />
        <meta name="twitter:card" content="summary_large_image" /> */}
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">Home</Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">Honda Two-Wheeler Insurance</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">Honda Two-Wheeler Insurance </span> 
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                Honda is known for its smooth performance, durability, and stylish range of two-wheelers. Whether you ride a commuter-friendly Shine or a premium CB350, protecting your bike starts with the right insurance coverage. This guide takes you through everything you need to know about insuring a Honda bike, from average premiums and model insights to useful add-ons, renewal tips, and frequently asked questions.
                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="../assets/hondainsurance1.jpeg"
                alt="Honda Two-Wheeler"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-center items-center p-4 mt-6">
        <div className="bg-white shadow-lg rounded-2xl p-6 max-w-7xl text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
            About Honda in India
          </h2>
          <p className="text-gray-600 leading-relaxed">
            Honda has built its reputation in India with reliable engines, fuel-efficient performance and strong resale value. Its lineup includes commuter bikes like Shine, Dream / Dream Neo, sporty models like CB Hornet and Unicorn, and top-selling scooters such as Activa, Dio and Elevate.
          </p>
          <p className="text-gray-600 leading-relaxed mt-3">
            Honda’s extensive dealer and service network across the country makes it a go-to choice for first-time riders and daily commuters. Insuring your Honda not only safeguards you financially but also ensures smoother claim settlements and access to authorized workshop support.
          </p>
        </div>
      </div>
       <div className="p-4 sm:p-8 bg-white">
                                                        <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
                                                          Choose Your Bike Brand
                                                        </h2>
                                                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                                                          {hondaInsurance.map((calc, index) => (
                                                            <motion.div
                                                              key={index}
                                                              whileHover={{ scale: 1.04 }}
                                                              initial={{ opacity: 0, y: 20 }}
                                                              animate={{ opacity: 1, y: 0 }}
                                                              transition={{ duration: 0.3, delay: index * 0.05 }}
                                                            >
                                                              <Link
                                                                to={calc.path}
                                                                className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
                                                              >
                                                                <img
                                                                  src={calc.image}
                                                                  alt={calc.name}
                                                                  className="w-16 h-16 object-contain mb-2"
                                                                />
                                                                <span className="text-md font-medium text-gray-800 break-words leading-snug">
                                                                  {calc.name}
                                                                </span>
                                                              </Link>
                                                            </motion.div>
                                                          ))}
                                                        </div>
                                                      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-400 opacity-20 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-400 opacity-20 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-5xl mx-auto bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl p-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Why Honda Owners Need Two-Wheeler Insurance
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            Even with a mechanically reliable vehicle, owning a two-wheeler without insurance risks financial loss in cases of:
          </p>
          <ul className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 text-gray-700 mb-6">
            <li className="flex items-start gap-3">
              <FaCarCrash className="text-xl text-red-500 mt-1" />
              <span>Road accidents or collisions</span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserLock className="text-xl text-yellow-500 mt-1" />
              <span>Theft, vandalism or fire</span>
            </li>
            <li className="flex items-start gap-3">
              <FaCloudSunRain className="text-xl text-blue-500 mt-1" />
              <span>Flood, storm and natural calamities</span>
            </li>
            <li className="flex items-start gap-3">
              <FaShieldAlt className="text-xl text-green-500 mt-1" />
              <span>Third-party bodily injury or liability</span>
            </li>
            <li className="flex items-start gap-3">
              <FaTools className="text-xl text-indigo-500 mt-1" />
              <span>Damage to Honda’s genuine components</span>
            </li>
          </ul>
          <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-lg text-indigo-700">
            A comprehensive two-wheeler policy limits financial exposure and gives you access to network garages for speedy cashless repairs.
          </div>
        </motion.div>
      </section>
        <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
           <div className="flex flex-col md:flex-row justify-between items-center gap-10">
             <div className="max-w-xl space-y-6 text-center md:text-left">
               <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
                 Calculate Your TwoWheeler Insurance <br />
                 in <span className="text-blue-600">Seconds</span>
               </h2>
               <p className="text-gray-700 text-lg">
                 Use our fast and free calculator to estimate your premium — no paperwork, no waiting.
               </p>
               <Link
                 to="/financial-tools-calculators/bike-insurance-calculator"
                 className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
               >
                 Start Calculating <ArrowRight className="w-5 h-5" />
               </Link>
             </div>
             <div className="space-y-4 text-left">
               {features.map((item, index) => (
                 <div key={index} className="flex items-center gap-3">
                   <span className="text-xl" role="img" aria-label={item.text}>{item.icon}</span>
                   <span className="text-gray-800 font-medium">{item.text}</span>
                 </div>
               ))}
             </div>
           </div>
         </section>
      <div className="container mx-auto p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Popular Honda Models and Insurance Cost Estimates
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Here’s an approximate breakdown of key Honda two-wheeler models in India, their IDV range and starting premium estimates:
        </p>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              { model: "Honda Shine / Shine SP", category: "Commuter Bike", idv: "65,000 – 85,000", premium: "₹800 – ₹1,200" },
              { model: "Honda CB Shine / Unicorn", category: "Evolution Bike", idv: "80,000 – 1 Lakh", premium: "₹900 – ₹1,300" },
              { model: "CB Hornet 160R", category: "Sports Commuter", idv: "1.1 – 1.3 Lakh", premium: "₹1,200 – ₹1,800" },
              { model: "Honda Activa", category: "Popular Scooter", idv: "75,000 – 95,000", premium: "₹950 – ₹1,350" },
              { model: "Honda Dio", category: "Lifestyle Scooter", idv: "75,000 – 90,000", premium: "₹900 – ₹1,300" },
              { model: "Honda Elevate", category: "Compact Scooter", idv: "85,000 – 1 Lakh", premium: "₹1,000 – ₹1,400" },
              { model: "Honda CB 200X", category: "Adventure Commuter", idv: "1.2 – 1.4 Lakh", premium: "₹1,300 – ₹1,900" },
            ].map((item, index) => (
              <div key={index} className="border mb-4 p-4 rounded-lg bg-white shadow-md">
                <h3 className="font-semibold text-gray-800 mb-2">{item.model}</h3>
                <div className="text-gray-600 mb-1"><strong>Category:</strong> {item.category}</div>
                <div className="text-gray-600 mb-1"><strong>Approx. IDV:</strong> {item.idv}</div>
                <div className="text-gray-600"><strong>Starting Premium:</strong> {item.premium}</div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">Honda Model</th>
                  <th className="p-3 border border-gray-300 text-left">Type</th>
                  <th className="p-3 border border-gray-300 text-left">Approx. IDV (₹)</th>
                  <th className="p-3 border border-gray-300 text-left">Starting Premium (₹)</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  ["Honda Shine / Shine SP", "Commuter Bike", "65,000 – 85,000", "₹800 – ₹1,200"],
                  ["Honda CB Shine / Unicorn", "Evolution Bike", "80,000 – 1 Lakh", "₹900 – ₹1,300"],
                  ["CB Hornet 160R", "Sports Commuter", "1.1 – 1.3 Lakh", "₹1,200 – ₹1,800"],
                  ["Honda Activa", "Popular Scooter", "75,000 – 95,000", "₹950 – ₹1,350"],
                  ["Honda Dio", "Lifestyle Scooter", "75,000 – 90,000", "₹900 – ₹1,300"],
                  ["Honda Elevate", "Compact Scooter", "85,000 – 1 Lakh", "₹1,000 – ₹1,400"],
                  ["Honda CB 200X", "Adventure Commuter", "1.2 – 1.4 Lakh", "₹1,300 – ₹1,900"],
                ].map(([model, category, idv, premium], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{model}</td>
                    <td className="p-3 border border-gray-300">{category}</td>
                    <td className="p-3 border border-gray-300">{idv}</td>
                    <td className="p-3 border border-gray-300">{premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Useful Add-Ons for Honda Two-Wheelers
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Enhance your base insurance with these value-added covers:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6 mt-4">
          {cardsNew.map((card, index) => (
            <div
              key={index}
              className="border rounded-lg shadow-lg p-6 flex flex-col items-center text-center"
            >
              {card.icon}
              <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-gray-600">{card.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="mx-auto container p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Best Insurance Providers for Honda Two-Wheelers
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          These insurers offer reliable services, quick claims and wide two-wheeler coverage across India:
        </p>
        <div className="mt-6 grid md:grid-cols-3 gap-6 text-left">
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">⚡</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">New India Assurance</h3>
            <p className="text-gray-700 text-sm mt-1">Trusted PSU with strong rural and national network.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🚗</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">Digit Insurance</h3>
            <p className="text-gray-700 text-sm mt-1">Digital-first, popular among urban riders for ease of use.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🏥</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">Bajaj Allianz</h3>
            <p className="text-gray-700 text-sm mt-1">Extensive cashless garage network and efficient service.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">💰</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">IFFCO Tokio</h3>
            <p className="text-gray-700 text-sm mt-1">Affordable and approachable for first-time buyers.</p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">📱</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">Tata AIG</h3>
            <p className="text-gray-700 text-sm mt-1">Great add-on packages and prompt claim support.</p>
          </div>
        </div>
      </div>
      <div className="w-full px-2 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Tips to Reduce Your Honda Insurance Premium
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              You can lower costs without compromising coverage by following these tips:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Build and retain No Claim Bonus (NCB) over years.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Opt for a voluntary deductible if you’re a careful rider.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Avoid small claims to preserve NCB benefits.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Install ARAI-certified anti-theft devices to claim additional discounts.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Compare multiple insurer quotes before renewing.</span>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-emerald-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <BadgeCheck className="w-8 h-8 text-emerald-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Required for Honda Bike or Scooter Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              Ensure you have these documents on hand when purchasing or renewing:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Registration Certificate (RC) of your Honda two-wheeler</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Previous insurance policy document (if renewing)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Valid Pollution Under Control (PUC) certificate</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>KYC documents like Aadhaar or PAN</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl sm:col-span-2">
                <span>Passport-sized photo (usually required only for offline applications)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          Honda Two-Wheeler Insurance FAQs
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div id={`faq-answer-${index}`} className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <FaCarSide />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                Honda two-wheelers offer reliability, fuel efficiency and trusted service across India. Whether you ride a Shine, Activa, Hornet or Elevate, choosing the right insurance plan ensures you stay protected from accidents, theft or damage.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                At TheFinMaster, we help you compare policies, select the best add-ons and make smart insurance choices aligned with your Honda bike or scooter.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default HondaTwoWheelerInsurance;